import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { createFileLogger } from '../../../../utils/logger';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';

const log = createFileLogger('CANVAS_COMMANDS');

export const canvasCreateCommand: SelfContainedCommand = {
  id: 'canvas_create',
  description: '🎨 CREATE: Create a new canvas for visual collaboration - sticky notes, drawings, and images',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Title for the new canvas (default: "Untitled Canvas")',
        required: false,
        defaultValue: 'Untitled Canvas',
        validator: (value: string) => {
          if (value && value.trim().length === 0) return 'Canvas title cannot be empty';
          if (value && value.length > 100) return 'Canvas title too long (max 100 chars)';
          return null;
        },
        examples: ['Brainstorm Session', 'Design Ideas', 'Project Planning']
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'The ID of the space to create the canvas in. Will use context spaceId if not provided.',
        required: false,
        examples: ['space-123', 'space-abc-def']
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'The ID of the folder to create the canvas in. Leave empty for root level.',
        required: false,
        examples: ['folder-123', null]
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { title: 'Design Brainstorm' },
        expectedOutput: createSuccessResult('Canvas created successfully', {
          canvas: {
            id: 'canvas-123',
            title: 'Design Brainstorm',
            entityType: 'Canvas'
          }
        }),
        description: 'Create a new canvas with a custom title',
        context: 'When starting a new visual brainstorming session'
      }
    ],
    bad: [
      {
        input: { title: '' },
        error: 'Canvas title cannot be empty',
        why: 'Empty titles are not allowed',
        fix: 'Provide a non-empty title or omit the parameter to use default'
      }
    ]
  },
  
  performance: {
    expectedDuration: 500,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { title = 'Untitled Canvas', spaceId: providedSpaceId, folderId } = params;
    const spaceId = providedSpaceId || context.spaceId;
    
    try {
      log.log('[canvas_create] Creating new canvas:', { title, spaceId });
      
      const now = new Date().toISOString();
      const newCanvasEntityData = {
        entityType: 'Canvas',
        name: title.trim(),
        icon: '🎨',
        spaceId: spaceId,
        skeleton: {
          '@type': 'Canvas',
          // ✅ v2 Canvas (unified architecture)
          version: 'v2',
          architecture: 'unified',
          elements: [],
          viewport: { x: 0, y: 0, zoom: 1 },
          width: 5000,
          height: 5000,
          backgroundColor: '#ffffff',
          createdAt: now,
          updatedAt: now,
          groupIds: folderId ? [folderId] : []  // ✅ EDGE CREATION: Sets parent for automatic edge creation
        },
        created_at: now
      };
      
      log.log('[canvas_create] Creating canvas via entityActions:', { 
        spaceId: newCanvasEntityData.spaceId,
        title: newCanvasEntityData.name
      });
      
      // Use store actions for proper event emission and sync
      const entityStoreModule = await import('../../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();
      
      const newCanvas = await entityStore.actions.createEntity(newCanvasEntityData);
      log.log('[canvas_create] Canvas entity created:', { 
        id: newCanvas._id, 
        name: newCanvas.name
      });
      
      // Register llmId mapping
      const canvasLlmId = newCanvas.llmId;
      const canvasActualId = newCanvas._id || newCanvas.id;
      if (canvasLlmId && canvasActualId) {
        const { llmIdMapper } = await import('../../../../utils/llmIdMapper');
        llmIdMapper.registerMapping(canvasLlmId, canvasActualId, 'document', 'Canvas', newCanvas.name);
        log.log('[canvas_create] ✅ Registered llmId mapping:', { canvasLlmId, canvasActualId });
      }
      
      // Register with dataspace tree for immediate positioning
      if (canvasActualId && spaceId) {
        try {
          const parentId = folderId || spaceId;
          
          const { default: useDataspaceTreeStore } = await import('../../../../stores/dataspaceTreeStore');
          const treeStore = useDataspaceTreeStore.getState();
          const edgeKey = `${parentId}:${canvasActualId}`;
          
          treeStore._applyAddNode(edgeKey, {
            id: canvasActualId,
            parentId: parentId,
            pos: "M",
            expanded: false,
            kind: 'canonical'
          });
          
          log.log('[canvas_create] ✅ Added to dataspace tree:', { folderId: parentId, canvasId: canvasActualId });
        } catch (treeError) {
          log.warn('[canvas_create] ⚠️ Failed to add to tree (non-fatal):', treeError);
        }
      }
      
      // Open window for the canvas
      let windowInfo = null;
      
      try {
        const { useCommandRegistryStore } = await import('../../../../stores/commandRegistryStore');
        const { executeCommand } = useCommandRegistryStore.getState();
        
        const entityId = newCanvas._id || newCanvas.id;
        const entityLlmId = newCanvas.llmId;
        
        const result = (await executeCommand('space_open_window', {
          entityId
        })) as import('../../../../types/standardToolResults').StandardToolResult;
        
        if (result.status === 'success') {
          const windowId = result.data?.windowId;
          log.log('[canvas_create] ✅ Window opened successfully:', windowId);
          
          windowInfo = {
            id: windowId,
            title: title.trim(),
            appType: 'canvas-v2',  // ✅ Changed to v2
            entityId,
            entityLlmId
          };
        } else {
          log.error('[canvas_create] Failed to open window:', result.message || 'Unknown error');
        }
      } catch (windowError) {
        log.error('[canvas_create] ❌ Failed to create window:', windowError);
      }
      
      const result = {
        entityId: newCanvas._id || newCanvas.id, // ✅ STANDARDIZED: For sidebar rename extraction
        canvas: {
          id: newCanvas.id || newCanvas._id,
          llmId: (newCanvas as any).llmId,
          title: title.trim(),
          entityType: newCanvas.entityType || 'Canvas',
          created_at: newCanvas.created_at,
          icon: newCanvas.icon || '🎨'
        },
        window: windowInfo
      };
      
      log.log('[canvas_create] ✅ Canvas created successfully:', result);
      
      return createSuccessResult(
        `Canvas created successfully: "${title.trim()}"${windowInfo ? ' (with window)' : ''} Canvas is ready for use.`,
        result,
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'self-contained',
            affectedEntities: [newCanvas.id || newCanvas._id, ...(windowInfo ? [windowInfo.id] : [])]
          } as any,
          ui: {
            refreshContext: true,
            autoFocus: windowInfo?.id,
            resultPreview: 'summary'
          }
        }
      );
      
    } catch (error: any) {
      log.error('[canvas_create] Error creating canvas:', error);
      return createErrorResult(
        'Canvas creation failed',
        `Failed to create canvas: ${error.message || 'Unknown error'}`,
        'execution',
        { 
          data: { 
            title,
            spaceId,
            errorStack: error instanceof Error ? error.stack : undefined
          },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

// ✅ Register this command in the entity creation registry
// This allows EntitySidebar to discover it without hardcoding Canvas schemas
entityCreationRegistry.register({
  commandId: 'canvas_create',
  entityType: 'Canvas',
  displayName: 'Canvas',
  icon: {
    type: 'phosphor',
    phosphorIcon: 'PaintBrush',
    phosphorWeight: 'duotone'
  },
  description: 'Create a visual canvas for brainstorming',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'other',
  sortOrder: 3, // After documents and notes
  shortcut: undefined
});

